/**
 * Visit Clinic (施術入力 / 来院) hooks.
 * A2/R2 service.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, type QueryClient } from '@tanstack/react-query';
import {
  getYearmonthSearch,
  getVisitClinicSchedule,
  storeVisitClinicSchedule,
  deleteVisitClinicSchedule,
} from '@/services/main/visitClinic';
import { useAuth } from '@/shared/providers/AuthContext';
import { getApiErrorMessage } from '@/utils/apiError';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import type {
  VisitClinicScheduleData,
  VisitClinicStorePayload,
} from '@/types/visitClinic.types';

function buildVisitClinicMassagerLabel(
  massager: VisitClinicScheduleData['massagers'][number],
  serviceType?: string
): string {
  const serialNumber = massager.serial_number == null ? '' : String(massager.serial_number);
  const address = [massager.address1, massager.address2, massager.address3]
    .filter(Boolean)
    .join('');

  if (serviceType?.toUpperCase() === SERVICE_TYPE_R2) {
    return `${serialNumber}:${massager.name}:${address || massager.address || ''}`;
  }

  const practitioner = [serialNumber, massager.name].filter(Boolean).join(' ');
  const locationName = massager.location_name ?? '';
  const location = `${address}${locationName ? `　${locationName}` : ''}`;
  return `${practitioner}　:　${location || massager.address || ''}`;
}

function normalizeVisitClinicScheduleData(
  data: VisitClinicScheduleData,
  serviceType?: string
): VisitClinicScheduleData {
  return {
    ...data,
    massagers: data.massagers.map(massager => ({
      ...massager,
      label: buildVisitClinicMassagerLabel(massager, serviceType),
    })),
  };
}

/**
 * 同意書の登録・更新・削除後に施術入力カレンダー GET を再取得させる.
 * グローバル staleTime(1h) のままだと consents / visits が古いまま残るため使用する.
 */
export function invalidateVisitClinicScheduleForPatient(
  queryClient: QueryClient,
  patientId: number | string
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: q =>
      Array.isArray(q.queryKey) &&
      q.queryKey[0] === 'visitClinic' &&
      q.queryKey[1] === 'schedule' &&
      String(q.queryKey[4] ?? '') === String(patientId),
  });
}

/**
 * 施術入力カレンダーを保存・削除後に即座にローディング状態に戻す.
 * resetQueries はキャッシュを pending に戻し自動 refetch するため、
 * 古いデータが一瞬表示される flash を防ぐ (refetch() と異なる点).
 */
export function resetVisitClinicScheduleForPatient(
  queryClient: QueryClient,
  patientId: number | string
): Promise<void> {
  return queryClient.resetQueries({
    predicate: q =>
      Array.isArray(q.queryKey) &&
      q.queryKey[0] === 'visitClinic' &&
      q.queryKey[1] === 'schedule' &&
      String(q.queryKey[4] ?? '') === String(patientId),
  });
}

/**
 * 受診者の最終施術年月を取得する mutation hook.
 * 検索ヒット後に呼び出し、返却された yearmonth で editInit ページへ遷移する.
 */
export function useVisitClinicYearmonthSearch() {
  const { clinic } = useAuth();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (patientId: number) =>
      getYearmonthSearch(
        clinic?.site_type ?? '',
        clinic?.clinic_number ?? '',
        patientId,
        serviceType
      ),
    onError: err => {
      toast.error(getApiErrorMessage(err, '施術年月の取得に失敗しました。'));
    },
  });
}

/**
 * 施術入力カレンダーデータを取得する query hook.
 * Enabled when patientId and yearmonth are provided.
 */
export function useVisitClinicScheduleQuery(
  patientId: number | null,
  yearmonth: string | null,
  serviceType: string
) {
  const { clinic } = useAuth();
  const toast = useAppToast();

  const query = useQuery({
    queryKey: [
      'visitClinic',
      'schedule',
      clinic?.site_type,
      clinic?.clinic_number,
      patientId,
      yearmonth,
      serviceType,
    ],
    queryFn: async () => {
      const data = await getVisitClinicSchedule(
        clinic?.site_type ?? '',
        clinic?.clinic_number ?? '',
        patientId!,
        yearmonth!,
        serviceType
      );
      return normalizeVisitClinicScheduleData(data, serviceType);
    },
    enabled: !!patientId && !!yearmonth && !!clinic?.site_type && !!clinic?.clinic_number,
    retry: false,
    // 同意書・施術は他画面更新と直結するため、グローバル 1h stale を上書きする
    staleTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (query.isError && query.error) {
      toast.error(getApiErrorMessage(query.error, '施術データの取得に失敗しました。'));
    }
  }, [query.isError, query.error, toast]);

  return query;
}

/**
 * 施術入力カレンダーを保存する mutation hook.
 */
export function useVisitClinicStoreMutation(patientId: number, yearmonth: string, serviceType: string) {
  const { clinic } = useAuth();
  const toast = useAppToast();
  const modal = useAppModal();

  return useMutation({
    // Opt out of global 404 toast (データが見つかりません。) from MutationCache —
    // onError below already shows the BE message via getApiErrorMessage.
    // Also opt out of global query invalidation on 404 to preserve local UI
    // state (selected main_massager, pending edits) while the user retries.
    meta: { suppressNotFoundToast: true, suppressNotFoundInvalidate: true },
    mutationFn: (payload: VisitClinicStorePayload) =>
      storeVisitClinicSchedule(
        clinic?.site_type ?? '',
        clinic?.clinic_number ?? '',
        patientId,
        yearmonth,
        payload,
        serviceType
      ),
    onSuccess: () => {
      toast.success('更新が正常に終了しました。');
    },
    onError: (err: unknown) => {
      const message = getApiErrorMessage(err, '更新ができませんでした。');
      toast.error('更新ができませんでした。');
      modal.error({
        title: message,
        okText: 'OK',
      });
    },
  });
}

/**
 * 一括削除 mutation hook.
 */
export function useVisitClinicBulkDeleteMutation(patientId: number, yearmonth: string, serviceType: string) {
  const { clinic } = useAuth();
  const toast = useAppToast();
  const modal = useAppModal();

  return useMutation({
    mutationFn: () =>
      deleteVisitClinicSchedule(
        clinic?.site_type ?? '',
        clinic?.clinic_number ?? '',
        patientId,
        yearmonth,
        serviceType
      ),
    onSuccess: () => {
      // BD §5.3: success → toast message
      toast.success('削除が正常に終了しました。');
    },
    onError: (err: unknown) => {
      // BD §5.3 fallback: BE may still return validation errors for lock / 訪問入力
      const e = err as { response?: { data?: { errors?: Record<string, string[]> } } };
      const list = e?.response?.data?.errors?.visits;
      if (Array.isArray(list) && list.length > 0) {
        list.forEach(m =>
          modal.error({
            title: m,
            okText: 'OK',
          })
        );
        return;
      }
      modal.error({
        title: getApiErrorMessage(err, '削除に失敗しました。'),
        okText: 'OK',
      });
    },
  });
}
